import {
  Badge,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { ActionButton } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FormModal } from '../../../../components/FormModal'
import { Icon } from '../../../../components/Icon'
import { TableSkeletonRows } from '../../../../components/Loading'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { Tooltip } from '../../../../components/Tooltip'
import { ViewOnSiteButton } from '../../../../components/ViewOnSiteButton'
import { buildEventPreviewUrl, formatDate, isEventPast, WEBSITE_URL, useAppToast } from '../../../../helpers'
import { EventSeriesService } from '../../../../services/Event/EventSeriesService'
import { EventService } from '../../../../services/Event/EventService'
import { Event, EventComplete, EventFormat } from '../../../../services/Event/types'
import { EventSeriesFormModal } from './EventSeriesFormModal'
import { SeriesPickerModal } from './SeriesPickerModal'

const formats: Record<EventFormat, string> = {
  IN_PERSON: 'Presencial',
  ONLINE: 'Online',
  HYBRID: 'Híbrido'
}

interface IProps {
  isOpen: boolean
  onClose: () => void
  seriesId: number
  seriesName: string
  canEdit?: boolean
  /** Opens the series form; pass an edition id to jump straight to that edition's card. */
  onEditSeries?: (editionId?: number) => void
  /** Called after an edition is moved/removed, so the parent list can refresh. */
  onChanged?: () => void
}

/** Lists a series' editions as individual events (the "Ver edições" modal). */
export function EventEditionsModal({
  isOpen,
  onClose,
  seriesId,
  seriesName,
  canEdit,
  onEditSeries,
  onChanged
}: IProps) {
  const service = new EventSeriesService()
  const eventService = new EventService()
  const toast = useAppToast()
  const [editions, setEditions] = useState<Event[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [moveTarget, setMoveTarget] = useState<Event | null>(null)
  const [removeTarget, setRemoveTarget] = useState<Event | null>(null)
  const [removing, setRemoving] = useState(false)
  const [seriesFromEvent, setSeriesFromEvent] = useState<EventComplete>()

  const loadEditions = () => {
    setLoading(true)
    setLoadError(false)
    service
      .getEditions(seriesId)
      .then((response) => setEditions(response.data))
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar as edições', error)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!isOpen) return
    loadEditions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, seriesId])

  const handleMove = async (targetSeriesId: number) => {
    if (!moveTarget) return
    try {
      const { message } = await service.attachEvent(targetSeriesId, moveTarget.id)
      toast.success(message ?? 'Evento movido')
      setMoveTarget(null)
      loadEditions()
      onChanged?.()
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      toast.error('Não foi possível mover o evento', error)
    }
  }

  // Open the series creation form pre-filled from this edition; on save a new
  // series is created and the edition is moved into it (keeping all its data).
  const handleCreateSeriesFromEdition = async () => {
    if (!moveTarget) return
    try {
      const fullEvent = await eventService.get(moveTarget.id)
      setMoveTarget(null)
      setSeriesFromEvent(fullEvent)
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      toast.error('Não foi possível abrir a criação de série', error)
    }
  }

  const handleRemove = async () => {
    if (!removeTarget) return
    setRemoving(true)
    try {
      const { message } = await service.detachEvent(seriesId, removeTarget.id)
      toast.success(message ?? 'Evento removido da série')
      setRemoveTarget(null)
      loadEditions()
      onChanged?.()
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      toast.error('Não foi possível remover o evento da série', error)
    } finally {
      setRemoving(false)
    }
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={`Edições — ${seriesName}`}
        size="wide"
        footer={
          <>
            {canEdit && onEditSeries && (
              <Button colorScheme="magenta" mr={3} onClick={() => onEditSeries()}>
                Editar série
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>
              Fechar
            </Button>
          </>
        }
      >
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>Edição</Th>
                <Th>Nome</Th>
                <Th>Formato</Th>
                <Th>Tipos</Th>
                <Th>Data</Th>
                <Th>Ingressos</Th>
                <Th>Visível</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <TableSkeletonRows cols={8} />
              ) : loadError ? (
                <TableErrorRow colSpan={8} entity="edições" feminine onRetry={loadEditions} />
              ) : editions.length === 0 ? (
                <TableEmptyRow colSpan={8} entity="edição" feminine />
              ) : (
                editions.map((edition) => (
                  <Tr key={edition.id}>
                    <Td>
                      {/* The badge took the place of the ID column, and this modal was the
                      only screen exposing an edition's id — keep it one hover away. */}
                      {edition.edition_number ? (
                        <Tooltip label={`ID ${edition.id}`}>
                          <Badge colorScheme="purple" py={1} cursor="help">
                            Edição {edition.edition_number}
                          </Badge>
                        </Tooltip>
                      ) : (
                        <Text variant="caption">-</Text>
                      )}
                    </Td>
                    <Td>
                      <Link to={`/events/${edition.slug}`} style={{ color: '#3182CE' }}>
                        {edition.name}
                      </Link>
                    </Td>
                    <Td>{formats[edition.format]}</Td>
                    <Td>
                      <Flex gap={1} flexWrap="wrap">
                        {edition.types && edition.types.length > 0 ? (
                          edition.types.map((type) => (
                            <Badge key={type.id} colorScheme="cyan" py={1}>
                              {type.name}
                            </Badge>
                          ))
                        ) : (
                          <Text variant="caption">-</Text>
                        )}
                      </Flex>
                    </Td>
                    <Td>
                      {formatDate(edition.date_start)} {edition.time_start}
                    </Td>
                    <Td>
                      {edition.total_tickets === 0 ? (
                        <Text color="gray.400">-</Text>
                      ) : (
                        <Text fontWeight="600" whiteSpace="nowrap">
                          {edition.total_participants}/{edition.total_tickets}
                        </Text>
                      )}
                    </Td>
                    <Td>{edition.visible ? 'Sim' : 'Não'}</Td>
                    <Td>
                      {(() => {
                        const past = isEventPast(edition)
                        const showLive = edition.visible && !past
                        const label = showLive
                          ? 'Ver no site'
                          : past && edition.visible
                            ? 'Abrir preview (evento encerrado)'
                            : 'Abrir preview'
                        return (
                          <ViewOnSiteButton
                            href={
                              showLive
                                ? `${WEBSITE_URL}/eventos/${edition.slug}`
                                : buildEventPreviewUrl(WEBSITE_URL, edition)
                            }
                            label={label}
                            isGhost={!showLive}
                          />
                        )
                      })()}
                      <ActionButton action="view" to={`/events/${edition.slug}`} />
                      <ActionButton action="settings" label="Painel do Evento" to={`/events/${edition.slug}/admin`} />
                      {canEdit && (
                        <Menu>
                          <MenuButton as={IconButton} aria-label="Opções" size="sm" variant="ghost" ml={2}>
                            <Icon name="lni lni-line-dotted" transform="rotate(90deg)" />
                          </MenuButton>
                          <MenuList>
                            {onEditSeries && (
                              <MenuItem icon={<Icon name="lni lni-pencil" />} onClick={() => onEditSeries(edition.id)}>
                                Editar edição
                              </MenuItem>
                            )}
                            <MenuItem icon={<Icon name="lni lni-layers" />} onClick={() => setMoveTarget(edition)}>
                              Mover para outra série
                            </MenuItem>
                            <MenuItem
                              icon={<Icon name="lni lni-exit" />}
                              color="orange.500"
                              onClick={() => setRemoveTarget(edition)}
                            >
                              Remover da série (tornar avulso)
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      )}
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </FormModal>

      <SeriesPickerModal
        isOpen={!!moveTarget}
        onClose={() => setMoveTarget(null)}
        onConfirm={handleMove}
        onCreateSeries={handleCreateSeriesFromEdition}
        excludeSeriesId={seriesId}
        title={moveTarget ? `Mover "${moveTarget.name}" para outra série` : 'Mover para outra série'}
        confirmLabel="Mover para esta série"
        note="O evento mantém suas configurações, ingressos e inscritos. Apenas o valor mínimo de parcela passa a seguir o padrão da série de destino."
      />

      {seriesFromEvent && (
        <EventSeriesFormModal
          key={`series-from-${seriesFromEvent.id}`}
          isOpen={!!seriesFromEvent}
          sourceEvent={seriesFromEvent}
          onClose={() => {
            setSeriesFromEvent(undefined)
            loadEditions()
            onChanged?.()
          }}
        />
      )}

      <ConfirmDialog
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemove}
        action="delete"
        title="Remover da série"
        description={
          removeTarget
            ? `Remover "${removeTarget.name}" da série? Ele voltará a ser um evento avulso, mantendo todos os ingressos e inscritos.`
            : ''
        }
        confirmLabel="Remover"
        irreversible={false}
        isLoading={removing}
      />
    </>
  )
}
